import { exactPositive } from '@/lib/exact'
import type { ApiClient } from '@/lib/api'
import { getAnonymous } from '@/lib/api/client'
import { ApiError } from '@/lib/api/errors'
import { CELL_ROLES, type CellRole, type ConsoleConfig } from '@/lib/env/config'

/**
 * The staff session.
 *
 * # The console does not talk to Ory
 *
 * It calls `GET /v1/admin/me` and nothing else. The edge already verifies the
 * session against Ory once per request (go-wallet CLAUDE.md Part I §6); asking
 * Ory a second time from the browser would put a third party on the path of
 * rendering a page, and would need `oryapis.com` in the console's `connect-src`.
 *
 * Ory appears here in exactly two places, both TOP-LEVEL NAVIGATIONS rather
 * than fetches: sending an operator to log in, and signing them out. A
 * navigation is not governed by `connect-src`, so the CSP stays as tight as it
 * is.
 *
 * # Roles come from our directory, never from Ory
 *
 * Ory authenticates; it never authorizes (§6). The roles below are read from
 * `staff_users` / `staff_role_grants` by the backend and reported to us. If
 * they came from an Ory identity trait, granting compliance authority would be
 * an edit in a third party's console — outside this repo's audit trail, review
 * and versioning.
 *
 * # And the console is never the enforcement
 *
 * Everything here decides what to DRAW. go-wallet decides what may happen, on
 * every request, whether or not the console drew the button. A session that
 * said "admin" would still be refused by the backend if the directory
 * disagrees.
 */

/** The seven staff roles. Closed set — mirrors go-wallet's `staff.Role`. */
export const STAFF_ROLES = [
  'support',
  'ops',
  'compliance',
  'finance',
  'growth',
  'engineering',
  'admin',
] as const

export type StaffRole = (typeof STAFF_ROLES)[number]

/** The cell that answered, as `/me` reports it. */
export interface SessionCell {
  readonly id: string
  readonly role: CellRole
}

export interface StaffSession {
  /** The Ory identity id, as the audit log records it. */
  readonly subject: string

  /**
   * `staff_users.label` — what an admin typed. **EMPTY IS A REAL STATE.**
   *
   * It stays empty. The backend deliberately does not fall back to the subject
   * ("a console that prints an opaque uuid where a name belongs is showing the
   * operator something they cannot act on, and the masthead can decide what to
   * draw" — `admin_me.go`), and this module used to perform exactly the
   * substitution the backend refused. Deciding what to draw for an unnamed
   * operator is the masthead's job and it needs to be able to tell.
   */
  readonly displayName: string

  /**
   * Ours, and the reference an operator quotes about their own access.
   *
   * `null` when the backend did not send it — an older backend, or a shape
   * change. Not `0`: a staff id of zero is a row that does not exist, and
   * printing it would hand somebody a reference that resolves to nothing.
   */
  readonly staffId: number | null

  /** Resolved from OUR directory. May legitimately be empty: see below. */
  readonly roles: readonly StaffRole[]

  /**
   * What this principal can REACH — every permission key the backend's own
   * guard answers true for. The companion to `roles`, and not a substitute.
   *
   * `roles` is what was GRANTED and is the fact an access review reads. This is
   * what the grant IMPLIES, closed over `staff.implies` on the server, and it
   * is **the only one of the two a console may gate a control on** (go-wallet
   * #688). The two look interchangeable and are not: a compliance officer's
   * `roles` is `['compliance']` while their permissions include everything ops
   * and support hold, because those three roles nest — and `admin` implies
   * nothing at all, so an Admin session holds `handle.impersonation.decide`
   * only if it was granted compliance as well.
   *
   * Gating on roles here would therefore be wrong in both directions: it would
   * draw the handle decision for an Admin the server will refuse, and refuse it
   * to a Compliance officer on any screen whose role list forgot to name them.
   *
   * **A key absent because this backend has not deployed it yet reads as "not
   * held"**, which draws the control disabled — the safe direction, and the one
   * the API documents. Never open-coded to `true` for an unknown backend.
   *
   * Not a closed union: the enum lives in `x-staff-permission` on every
   * operation of `api/openapi-admin.yaml`, a route added there is gated
   * correctly with no console change, and a hand-copied list here is the second
   * implementation this field exists to delete.
   */
  readonly permissions: readonly string[]

  /**
   * The cell that ANSWERED, which is not the same claim as the cell this bundle
   * was configured with. `null` when the backend did not report one.
   */
  readonly cell: SessionCell | null

  /**
   * Where this backend says to send an operator to sign in and out.
   *
   * Published by `/me` precisely so the console does not carry a second copy of
   * a path owned by `authproxy.go`. `''` when it was not sent, which is what
   * makes the local constant a fallback rather than a competing answer.
   */
  readonly loginPath: string
  readonly logoutPath: string
}

/**
 * What the console knows about who is using it.
 *
 * `unknown` is a real state and not a synonym for signed-out. A console that
 * rendered "signed out" because an endpoint failed would send an authenticated
 * operator to log in again and again. Absence of an answer is not an answer.
 *
 * `/v1/admin/me` DOES exist now (go-wallet#284, shipped 2026-08-01); the 404
 * branch below is kept because a console deployed against an older backend must
 * still degrade honestly rather than claim a sign-out that did not happen.
 */
export type SessionState =
  | { readonly status: 'loading' }
  | { readonly status: 'signed-in'; readonly session: StaffSession }
  | { readonly status: 'signed-out' }
  | { readonly status: 'not-staff' }
  /**
   * A staff account that has been switched off.
   *
   * Distinct from `not-staff`, and the backend is specific about it
   * (`codeStaffDisabled`, 403). Collapsing the two tells someone whose access
   * was revoked this morning that they never had an account — which sends them
   * to the wrong person and reads as a bug rather than as a decision somebody
   * made. Revocation takes effect on the next request by design (the directory
   * read is uncached), so this is a state operators will actually see.
   */
  | { readonly status: 'disabled' }
  | { readonly status: 'unknown'; readonly reason: string }

interface MeResponse {
  readonly subject?: unknown
  readonly staff_id?: unknown
  readonly display_name?: unknown
  readonly roles?: unknown
  readonly permissions?: unknown
  readonly cell?: unknown
  readonly login_url?: unknown
  readonly logout_url?: unknown
}

/** A string field, or `''` when it did not arrive as one. Never substituted. */
function str(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

/**
 * The cell the backend answered from, or `null`.
 *
 * Fail-closed on the role: a value that is not one of the two known ones
 * resolves to `null` rather than to `primary`, because `primary` is the reading
 * under which every mutating control stays enabled. An unrecognised role is a
 * thing we do not know, and the honest rendering of not knowing is not the
 * permissive one.
 */
function parseCell(value: unknown): SessionCell | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null
  const raw = value as { id?: unknown; role?: unknown }
  const id = str(raw.id)
  const role = str(raw.role)
  if (id === '' || !(CELL_ROLES as readonly string[]).includes(role)) return null
  return { id, role: role as CellRole }
}

/**
 * `staff_id`, or `null`.
 *
 * Rejects zero and any non-integer. Zero is not a staff row, and rendering it
 * would hand an operator a reference to quote that resolves to nothing.
 */
function parseStaffId(value: unknown): number | null {
  return exactPositive(value)
}

function parseRoles(value: unknown): StaffRole[] {
  if (!Array.isArray(value)) return []
  const allowed = new Set<string>(STAFF_ROLES)
  // An unrecognised role is DROPPED, not passed through. A role string the
  // console does not know cannot be rendered meaningfully, and letting it
  // through would mean a typo in the directory silently widens what the UI
  // offers. Dropping it shows less than the operator may do, never more.
  return value.filter((r): r is StaffRole => typeof r === 'string' && allowed.has(r))
}

/**
 * The permission closure, as strings and nothing else.
 *
 * Unlike `parseRoles` there is no allow-list to filter against, and there must
 * not be one: the set of permission keys is declared per operation in the admin
 * OpenAPI document, grows with every route, and a copy of it here would silently
 * drop a key the day a new one ships — drawing a control disabled for an
 * operator who holds it. Non-strings are dropped because they cannot be compared
 * to a key; duplicates and unknown keys are harmless, since every lookup is an
 * exact match against a key the caller names.
 */
function parsePermissions(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.filter((p): p is string => typeof p === 'string' && p !== '')
}

export function parseSession(payload: unknown): StaffSession | null {
  if (typeof payload !== 'object' || payload === null) return null
  const raw = payload as MeResponse
  if (typeof raw.subject !== 'string' || raw.subject === '') return null
  return {
    subject: raw.subject,
    // Not `?? raw.subject`. See `StaffSession.displayName`: the backend refuses
    // that substitution on purpose and the console used to perform it anyway,
    // printing an opaque uuid where an operator's name belongs.
    displayName: str(raw.display_name),
    staffId: parseStaffId(raw.staff_id),
    roles: parseRoles(raw.roles),
    // Absent is `[]`, which reads as "holds nothing" and draws every gated
    // control disabled. That is the direction the API asks for and the only
    // safe one: a backend older than #688 sends no `permissions`, and inferring
    // the closure from `roles` here would be the second copy of `staff.implies`
    // that this field exists to delete.
    permissions: parsePermissions(raw.permissions),
    cell: parseCell(raw.cell),
    loginPath: str(raw.login_url),
    logoutPath: str(raw.logout_url),
  }
}

/**
 * Reads the caller's own session.
 *
 * Never throws for an expected outcome — an unauthenticated operator is not an
 * error condition, it is the state the login screen exists for.
 */
export async function fetchSession(api: ApiClient): Promise<SessionState> {
  try {
    const payload = await api.get<unknown>('/me')
    const session = parseSession(payload)
    if (session === null) {
      return { status: 'unknown', reason: 'the session endpoint returned a shape we do not know' }
    }
    return { status: 'signed-in', session }
  } catch (error) {
    if (!(error instanceof ApiError)) {
      return { status: 'unknown', reason: 'the backend could not be reached' }
    }
    if (error.status === 401) {
      // "No session." A valid session belonging to someone absent from the
      // staff directory comes back as 403, handled below — the backend is
      // specific about the difference and so is this.
      return { status: 'signed-out' }
    }
    if (error.status === 403) {
      return error.code === 'staff_disabled' ? { status: 'disabled' } : { status: 'not-staff' }
    }
    if (error.status === 404) {
      return { status: 'unknown', reason: 'this backend is older than /v1/admin/me' }
    }
    return { status: 'unknown', reason: `the session endpoint failed (${String(error.status)})` }
  }
}

/**
 * Where to send an operator to sign in.
 *
 * `return_to` brings them back to the page they were trying to reach, which is
 * the difference between "log in and continue" and "log in and start over".
 * Only same-origin paths are ever passed: an attacker-supplied `return_to` is
 * how a login flow becomes an open redirect.
 */
export function loginUrl(
  config: ConsoleConfig,
  returnTo: string,
  session: StaffSession | null = null,
  discovered: AuthPaths | null = null,
): string {
  return selfService(config, 'login', returnTo, session?.loginPath ?? '', discovered?.login ?? '')
}

export function logoutUrl(
  config: ConsoleConfig,
  returnTo: string,
  session: StaffSession | null = null,
  discovered: AuthPaths | null = null,
): string {
  return selfService(config, 'logout', returnTo, session?.logoutPath ?? '', discovered?.logout ?? '')
}

/**
 * Ory's self-service surface, reached through OUR origin.
 *
 * # Why not the identity provider's own URL
 *
 * This used to build on `config.orySdkUrl`, which is Ory Network's hostname.
 * Ory then set the session cookie on `…projects.oryapis.com` — a different
 * registrable domain from `tesora.money` — and a browser will never attach that
 * cookie to a request to `bck.…tesora.money`. The operator signed in
 * successfully, came back, and the backend saw no session. Every single time.
 *
 * go-wallet already solved this: it proxies Ory's self-service surface under
 * `/auth` on the API's own origin and strips the cookie's `Domain` attribute
 * (`internal/server/authproxy.go`), so the session cookie is host-only on
 * `bck.…tesora.money`. That cookie IS sent from `admin.…tesora.money`, because
 * `SameSite=Lax` is scoped to the SITE — both are `tesora.money` — even though
 * the origins differ. The console simply was not using it.
 *
 * # Why string concatenation and not `new URL(path, base)`
 *
 * Because the obvious fix is silently wrong:
 *
 *     new URL('/self-service/login/browser', 'https://bck…/auth')
 *       → 'https://bck…/self-service/login/browser'   // /auth is GONE
 *
 * A leading-slash path resolves against the ORIGIN, discarding the base's path.
 * That URL is not the proxy and answers 401. The prefix has to survive, so it
 * is joined as text.
 *
 * The paths are the ones the backend publishes in `/v1/admin/me` as `login_url`
 * and `logout_url` — see `AUTH_PREFIX`.
 */
function selfService(
  config: ConsoleConfig,
  flow: 'login' | 'logout',
  returnTo: string,
  published: string,
  discovered: string,
): string {
  const url = new URL(`${config.apiBaseUrl}${servicePath(flow, published, discovered)}`)
  url.searchParams.set('return_to', sameOriginReturn(returnTo))
  return url.toString()
}

/**
 * Three sources, in order of how well the backend knows the answer (#215).
 *
 * 1. what `/me` published — the backend that answered, for a signed-IN operator
 * 2. what `AUTH_PATHS_PATH` published — the same backend, for a signed-OUT one
 * 3. `AUTH_PREFIX` — this repository's copy, and now the last resort
 *
 * # This ordering is the bug admin-client#215 was about
 *
 * There used to be two sources and the second was the constant, which reads as
 * harmless redundancy and is not: a SIGNED-OUT operator has no `/me` answer, so
 * the constant decided **every first login** — the only login that matters to
 * somebody who cannot get in. It said `/auth`, the customer plane. Pressing
 * Sign in navigated to the customer sign-in page, minted a customer session
 * cookie, came back to a console looking for a staff one, and re-rendered
 * "Sign in". Navigation happened and nothing changed, which from the operator's
 * side is a dead button.
 *
 * # Why the constant could not simply be changed to the staff prefix
 *
 * `walletd` mounts `/staff-auth` only when `Config.SeparatesStaffPlane()` — a
 * staff Ory project configured AND different from the customer one. Staging
 * satisfies that; production does not and will not until go-wallet#802. A
 * hardcoded staff prefix gives production a sign-in button that 404s, which is
 * strictly worse than the bug it replaces because it cannot be mistaken for a
 * session problem.
 *
 * So the plane is LEARNED rather than assumed, and step 2 is what learns it.
 * The constant stays as the answer when the backend is too old to publish
 * anything — where it is still correct, because a backend without the anonymous
 * route is a backend without a separated staff plane.
 *
 * # Every source is validated the same way
 *
 * Only a same-origin absolute path is accepted. These are relative by design,
 * and anything else — a full URL, a protocol-relative `//host`, a traversal —
 * is refused rather than joined onto our API origin, because this value decides
 * where an operator is sent to authenticate. Validating the DISCOVERED path
 * matters more than validating the published one, not less: it is read before
 * any session exists.
 */
function servicePath(flow: 'login' | 'logout', published: string, discovered = ''): string {
  const constant = `${AUTH_PREFIX}/self-service/${flow}/browser`
  return usablePath(published) ?? usablePath(discovered) ?? constant
}

/** A same-origin absolute path, or `null` when it is not one. */
function usablePath(candidate: string): string | null {
  if (candidate === '') return null
  if (!candidate.startsWith('/') || candidate.startsWith('//')) return null
  if (candidate.includes('..')) return null
  return candidate
}

/**
 * Where go-wallet mounts the CUSTOMER identity surface.
 *
 * Mirrors `authPrefix` in `go-wallet/internal/server/authproxy.go`. Still a
 * constant rather than configuration, and that part of the original reasoning
 * stands: an operator console that could be pointed at an arbitrary sign-in
 * host is one misconfiguration away from collecting credentials for somebody
 * else. `CONSOLE_AUTH_PREFIX` was considered for #215 and rejected for exactly
 * that, and because a value mirrored in a pod's environment can disagree with
 * the backend silently.
 *
 * What changed at #215 is its STANDING, not its value. It used to be the
 * fallback for every signed-out operator, which made it the decider of every
 * first login; go-wallet#1024 gave staff their own plane at `/staff-auth` and
 * this constant then sent operators to the customer one. It is now the third
 * and last source in `servicePath`, reached only when the backend publishes
 * nothing — which is a backend with no separated staff plane, where `/auth` is
 * the right answer.
 *
 * It is deliberately NOT renamed to something like `CUSTOMER_AUTH_PREFIX`: it
 * is the fallback for a single-plane deployment, where the prefix serves both.
 */
export const AUTH_PREFIX = '/auth'

/**
 * Where the backend publishes the self-service paths to an ANONYMOUS caller.
 *
 * `/v1/admin/me` carries the same pair and is the better source, but it is
 * authenticated — so it cannot answer the one question a signed-out operator
 * has. This route exists to answer it, and go-wallet builds it from the same
 * `selfServiceURLs()` the authenticated one uses, so the two cannot drift.
 *
 * Absent on a backend older than that change. `fetchAuthPaths` reads that as
 * "nothing to learn" rather than as a failure — see there.
 */
export const AUTH_PATHS_PATH = '/v1/console/auth-paths'

/** The pair, as the console carries it. Relative paths on our own API origin. */
export interface AuthPaths {
  readonly login: string
  readonly logout: string
}

/**
 * Asks the backend which identity plane this deployment signs staff into.
 *
 * # Never throws, and never blocks a sign-in
 *
 * Every failure resolves to `null`, which sends `servicePath` to the constant —
 * the behaviour before #215, and correct on any deployment that has not
 * separated its planes. A 404 is the ordinary answer from a backend older than
 * the route and is not logged as a fault; a 500 or an unreachable backend is
 * the same outcome here, because in both cases we have learned nothing and the
 * question is only ever "did we learn a better path".
 *
 * The one exception is `AbortError`, rethrown so a cancelled navigation is not
 * mistaken for a backend that answered nothing.
 *
 * # It reads paths and nothing else
 *
 * A field that is not a same-origin absolute path is dropped rather than
 * carried — the validation is in `servicePath` and applies to this source too,
 * because this one is read before any session exists.
 */
export async function fetchAuthPaths(config: ConsoleConfig): Promise<AuthPaths | null> {
  let payload: unknown
  try {
    payload = await getAnonymous<unknown>(config, AUTH_PATHS_PATH)
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return null
  }
  if (typeof payload !== 'object' || payload === null) return null
  const o = payload as Record<string, unknown>
  const login = typeof o['login_url'] === 'string' ? o['login_url'] : ''
  const logout = typeof o['logout_url'] === 'string' ? o['logout_url'] : ''
  // Both or neither. A pair where sign-in goes to the staff plane and sign-out
  // to the customer one would leave a staff session alive after a logout that
  // reported success — the failure `selfServiceURLs` exists to prevent, and it
  // must not be reintroduced by half-reading its answer.
  if (usablePath(login) === null || usablePath(logout) === null) return null
  return { login, logout }
}

/**
 * Reduces any candidate to an absolute URL on OUR origin.
 *
 * Ory will echo `return_to` into a redirect, so anything reaching it must be
 * ours. Resolving against `window.location.origin` and then discarding the
 * result if the origin changed handles the cases a prefix check misses:
 * `//evil.example`, `https://evil.example`, and backslash variants.
 */
function sameOriginReturn(candidate: string): string {
  const origin = window.location.origin
  try {
    const resolved = new URL(candidate, origin)
    if (resolved.origin !== origin) return origin
    return resolved.toString()
  } catch {
    return origin
  }
}

/**
 * Whether a role set satisfies a screen's declared scope.
 *
 * # Case
 *
 * The design corpus writes `Ops` and `Compliance`; the directory writes `ops`
 * and `compliance`. Both are right for where they live — one is a label a
 * designer wrote, the other is the value `staff.ParseRole` accepts — and they
 * will keep disagreeing. Comparing them anywhere other than here means the next
 * comparison forgets, and a forgotten `toLowerCase` reads as "this operator
 * holds no roles" rather than as a bug.
 *
 * # What this is NOT
 *
 * It is not the permission model. It does not expand `compliance → ops →
 * support`, because that nest lives in go-wallet (`staff.implies`) and a copy
 * of it here is a second implementation that would be wrong in the permissive
 * direction the day the nest narrows — the failure `staff.Effective`'s own
 * comment exists to prevent. go-wallet#688 asks for the closure to be reported
 * so this console can render it instead of deriving it.
 *
 * Until then this answers the narrow question a DRAWING needs — does this
 * session hold one of the roles this screen is labelled with — and the console
 * never uses it to decide what a live screen may show. A screen the backend
 * answered is shown because the backend answered it.
 */
export function holdsAnyRole(
  held: readonly string[],
  required: readonly string[] | null | undefined,
): boolean {
  // No declared scope is not "no access"; it is a screen the design did not
  // scope, and those are visible to everyone.
  if (required === undefined || required === null || required.length === 0) return true
  const set = new Set(held.map((r) => r.toLowerCase()))
  // Admin is in the closed set of seven and is the one role the design treats
  // as universal. It is a fact about the corpus's own labelling, not an
  // expansion of the backend's model: no screen is labelled in a way that is
  // meant to exclude an admin.
  if (set.has('admin')) return true
  return required.some((r) => set.has(r.toLowerCase()))
}

/**
 * Whether this session may reach one permission key.
 *
 * The exact key, matched exactly, against the closure the server reported. No
 * prefix matching, no wildcard, no role arithmetic and deliberately no `admin`
 * shortcut — `holdsAnyRole` has one because the design corpus labels no screen
 * in a way meant to exclude an admin, and that is a fact about LABELS. It is not
 * true of the backend's grants: `admin` is absent from `staff.implies`, so an
 * Admin session that was never granted compliance does not hold
 * `handle.impersonation.decide`, and drawing the control for them would promise
 * a judgement the server refuses.
 *
 * `null` — no session, or one still loading — is "not held". Absence of an
 * answer is not an answer (the posture `SessionState.unknown` takes), and the
 * honest rendering of not knowing is the disabled control with the reason said
 * out loud, never the enabled one.
 *
 * This decides what to DRAW. go-wallet decides what may happen, on every
 * request, whether or not the console drew the button (Part I §5).
 */
export function holdsPermission(
  session: StaffSession | null,
  key: string,
): boolean {
  if (session === null) return false
  return session.permissions.includes(key)
}
